import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, ModalContent, ModalCloseButton } from '../common';
import { createGuest, createGuestAndAddToSession, updateGuest, fetchClubDefaultStartingElo } from '../../services/guestService';
import { ELO_CONSTANTS } from '../../utils/constants';
import ClubLeaderboardPreview from '../leaderboard/ClubLeaderboardPreview';

/**
 * @param {function(Object, { stayOpen?: boolean }=): void} [onGuestAdded] - `stayOpen` is true when adding
 *   (modal stays open for back-to-back guests until the user closes it).
 */
const AddGuestModal = ({ 
  isOpen, 
  onClose, 
  onGuestAdded,
  onGuestUpdated,
  guest: editingGuest,
  sessionId,
  clubId,
  currentUser,
  seasonId,
  /** Initial session_players.status when adding to a session */
  sessionEntryStatus = 'signed_up',
  /** Pre-fill name when opened from session add-player search */
  initialFirstName = '',
  initialLastName = '',
  /** When false, closes after each add instead of staying open for back-to-back guests. */
  stayOpenAfterAdd = true,
  /** Offline mode: create the guest on this phone and queue the server insert. */
  createGuestInSession,
}) => {
  const isSessionMode = Boolean(sessionId);
  const isEditMode = Boolean(editingGuest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEloGuide, setShowEloGuide] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [eloExpanded, setEloExpanded] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [clubDefaultElo, setClubDefaultElo] = useState(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
  const firstNameInputRef = useRef(null);
  const wasOpenRef = useRef(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    notes: '',
    mixedElo: ELO_CONSTANTS.DEFAULT_STARTING_ELO,
    levelsElo: ELO_CONSTANTS.DEFAULT_STARTING_ELO
  });

  // Reset form when the modal opens; fetch club default ELO first so fields match club settings.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!justOpened) return;

    setNotesExpanded(false);
    setEloExpanded(false);
    setShowEloGuide(false);
    setSuccessMessage(null);
    setError(null);

    let cancelled = false;

    const resetForm = async () => {
      const elo = clubId
        ? await fetchClubDefaultStartingElo(clubId)
        : ELO_CONSTANTS.DEFAULT_STARTING_ELO;

      if (cancelled) return;

      setClubDefaultElo(elo);

      if (editingGuest) {
        setFormData({
          firstName: editingGuest.first_name || '',
          lastName: editingGuest.last_name || '',
          gender: editingGuest.gender || 'male',
          notes: '',
          mixedElo: elo,
          levelsElo: elo
        });
      } else {
        setFormData({
          firstName: initialFirstName || '',
          lastName: initialLastName || '',
          gender: '',
          notes: '',
          mixedElo: elo,
          levelsElo: elo
        });
      }
    };

    resetForm();

    return () => {
      cancelled = true;
    };
  }, [isOpen, clubId, editingGuest, initialFirstName, initialLastName]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    
    if (!formData.gender) {
      setError('Gender is required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (isEditMode && editingGuest) {
        const updated = await updateGuest(editingGuest.id, {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName?.trim() || null,
          gender: formData.gender
        });
        if (onGuestUpdated) onGuestUpdated(updated);
        onClose();
      } else {
        const guestName = formData.firstName.trim();
        const guest = isSessionMode
          ? (typeof createGuestInSession === 'function'
              ? await createGuestInSession(formData, sessionEntryStatus)
              : await createGuestAndAddToSession(
                  formData,
                  sessionId,
                  clubId,
                  currentUser.id,
                  seasonId,
                  sessionEntryStatus
                ))
          : await createGuest(formData, clubId, currentUser.id, seasonId);
        const keepOpenAfterAdd = stayOpenAfterAdd && !isEditMode;
        if (onGuestAdded) {
          await onGuestAdded(guest, {
            stayOpen: keepOpenAfterAdd,
            addedSessionStatus: isSessionMode ? sessionEntryStatus : undefined,
          });
        }
        if (keepOpenAfterAdd) {
          setFormData({
            firstName: '',
            lastName: '',
            gender: '',
            notes: '',
            mixedElo: clubDefaultElo,
            levelsElo: clubDefaultElo
          });
          setNotesExpanded(false);
          setEloExpanded(false);
          setShowEloGuide(false);
          setError(null);
          setSuccessMessage(
            guestName
              ? `${guestName} added. Add another guest or close when you're done.`
              : 'Guest added. Add another guest or close when you\'re done.'
          );
          requestAnimationFrame(() => {
            firstNameInputRef.current?.focus();
          });
        } else {
          onClose();
        }
      }
    } catch (err) {
      console.error(isEditMode ? 'Error updating guest:' : 'Error adding guest:', err);
      setError(err.message || (isEditMode ? 'Failed to update guest' : 'Failed to add guest'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-border-medium rounded-lg bg-surface text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-surface-subtle disabled:cursor-not-allowed';
  const labelClass = 'block text-sm font-medium text-text-secondary mb-2';

  const mixedN = Number(formData.mixedElo);
  const levelsN = Number(formData.levelsElo);
  const eloIsDefault =
    !Number.isNaN(mixedN) &&
    !Number.isNaN(levelsN) &&
    mixedN === clubDefaultElo &&
    levelsN === clubDefaultElo;

  const eloSummary = eloIsDefault
    ? `${clubDefaultElo} default — expand to change`
    : `Mixed ${mixedN}, Levels ${levelsN}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="small">
        <ModalCloseButton onClose={onClose} />
        <h3 className="text-lg font-medium text-text-primary mb-2">
          {isEditMode ? 'Edit Guest Player' : 'Add Guest Player'}
        </h3>
        <p className="text-sm text-text-muted mb-4">
          {isEditMode
            ? 'Update the guest\'s name and gender.'
            : isSessionMode
              ? sessionEntryStatus === 'active'
                ? 'Add a guest as playing — they will join the queue for courts'
                : 'Add a guest player to this session (signed up first)'
              : 'Add a guest player to this club'}
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Message */}
          {successMessage && (
          <div className="mb-4 p-3 bg-success-light border border-success rounded-lg">
            <p className="text-success-dark text-sm">{successMessage}</p>
          </div>
        )}

        {error && (
            <div className="mb-4 p-3 bg-danger-light border border-danger rounded-lg">
              <p className="text-danger-dark text-sm">{error}</p>
            </div>
          )}

          {/* First & last name — side by side on wider viewports */}
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="add-guest-first-name">
                First Name *
              </label>
              <input
                ref={firstNameInputRef}
                id="add-guest-first-name"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="Enter first name"
                disabled={loading}
                autoComplete="given-name"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="add-guest-last-name">
                Last Name
              </label>
              <input
                id="add-guest-last-name"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="Enter last name"
                disabled={loading}
                autoComplete="family-name"
              />
            </div>
          </div>

          {/* Gender — quick pick (faster than a dropdown) */}
          <div className="mb-4">
            <div className={labelClass} id="add-guest-gender-label">
              Gender *
            </div>
            <div
              className="flex gap-2"
              role="group"
              aria-labelledby="add-guest-gender-label"
            >
              {[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ].map(({ value, label }) => {
                const selected = formData.gender === value;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, gender: value }))
                    }
                    aria-pressed={selected}
                    className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 ${
                      selected
                        ? value === 'female'
                          ? // Light: brand fuchsia. Dark: avoid female-light as text (it is a translucent tint token).
                            'border-female bg-female-light/40 text-female dark:border-fuchsia-400 dark:bg-fuchsia-500/20 dark:text-fuchsia-200'
                          : 'border-male bg-male/15 text-text-primary dark:bg-sky-500/15 dark:border-sky-400/70 dark:text-sky-200'
                        : 'border-border-medium bg-surface text-text-secondary hover:border-primary/40 hover:bg-surface-subtle'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ELO — collapsed by default; hide in edit mode */}
          {!isEditMode && (
            <div className="mb-4 rounded-lg border border-border-medium overflow-hidden">
              <button
                type="button"
                onClick={() => setEloExpanded((open) => !open)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left bg-surface-subtle/80 hover:bg-surface-subtle transition-colors dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
                aria-expanded={eloExpanded}
                aria-controls="add-guest-elo-panel"
                id="add-guest-elo-toggle"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-text-secondary">
                    Starting ELO
                  </span>
                  <span className="block text-xs text-text-muted truncate">
                    {eloSummary}
                  </span>
                </span>
                <span className="shrink-0 text-text-muted tabular-nums" aria-hidden>
                  {eloExpanded ? '−' : '+'}
                </span>
              </button>
              {eloExpanded && (
                <div
                  id="add-guest-elo-panel"
                  role="region"
                  aria-labelledby="add-guest-elo-toggle"
                  className="border-t border-border-medium px-3 py-3 space-y-3 bg-surface"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">
                        Mixed Doubles ELO
                      </label>
                      <input
                        type="number"
                        name="mixedElo"
                        value={formData.mixedElo}
                        onChange={handleInputChange}
                        min="800"
                        max="2000"
                        className={inputClass}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">
                        Levels Doubles ELO
                      </label>
                      <input
                        type="number"
                        name="levelsElo"
                        value={formData.levelsElo}
                        onChange={handleInputChange}
                        min="800"
                        max="2000"
                        className={inputClass}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-text-muted">
                    Default is {clubDefaultElo} (club setting). Range: 800–2000
                  </p>
                  {clubId && (
                    <div>
                      {!showEloGuide ? (
                        <button
                          type="button"
                          onClick={() => setShowEloGuide(true)}
                          className="text-xs text-primary hover:text-primary-dark font-medium"
                        >
                          Use club leaderboard as ELO guide →
                        </button>
                      ) : (
                        <div className="p-3 bg-surface-subtle rounded-lg border border-border-medium">
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <p className="text-xs font-medium text-text-secondary">
                              Club leaderboard — guide for starting ELO
                            </p>
                            <button
                              type="button"
                              onClick={() => setShowEloGuide(false)}
                              className="text-xs text-text-muted hover:text-text-primary shrink-0"
                            >
                              Hide
                            </button>
                          </div>
                          <ClubLeaderboardPreview
                            clubId={clubId}
                            seasonId={seasonId}
                            limit={8}
                            levelsGender={
                              formData.gender === 'female'
                                ? 'female'
                                : formData.gender === 'male'
                                  ? 'male'
                                  : null
                            }
                            compact
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes — collapsed by default; hide in edit mode */}
          {!isEditMode && (
            <div className="mb-6 rounded-lg border border-border-medium overflow-hidden">
              <button
                type="button"
                onClick={() => setNotesExpanded((open) => !open)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium text-text-secondary bg-surface-subtle/80 hover:bg-surface-subtle transition-colors dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
                aria-expanded={notesExpanded}
                aria-controls="add-guest-notes-panel"
                id="add-guest-notes-toggle"
              >
                <span>Notes (optional)</span>
                <span className="shrink-0 text-text-muted tabular-nums" aria-hidden>
                  {notesExpanded ? '−' : '+'}
                </span>
              </button>
              {notesExpanded && (
                <div
                  id="add-guest-notes-panel"
                  role="region"
                  aria-labelledby="add-guest-notes-toggle"
                  className="border-t border-border-medium px-3 py-3 bg-surface"
                >
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className={inputClass}
                    placeholder="Any additional notes about this guest..."
                    disabled={loading}
                  />
                </div>
              )}
            </div>
          )}

          {!isEditMode && stayOpenAfterAdd && (
            <p className="mb-4 text-sm text-text-muted">
              You can add several guests in a row. Close this window when you are done.
            </p>
          )}
          {!isEditMode && !stayOpenAfterAdd && isSessionMode && (
            <p className="mb-4 text-sm text-text-muted">
              After adding, you&apos;ll return to the player search to add more people.
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={onClose}
              variant="secondary"
              size="small"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
            >
              {loading
                ? (isEditMode ? 'Saving...' : 'Adding Guest...')
                : isEditMode
                  ? 'Save changes'
                  : isSessionMode
                    ? 'Add Guest to Session'
                    : 'Add Guest'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddGuestModal;
